import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  signInWithRedirect,
  signOut,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth'

export interface AuthUser {
  userId: string
  username: string
  email: string
  givenName: string | null
}

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  isSigningOut: boolean
  error: string | null

  // Actions
  initAuth: () => Promise<void>
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  clearAuth: () => void
  setError: (message: string) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      isSigningOut: false,
      error: null,

      /**
       * Attempt to restore an existing Cognito session.
       * Also called by the Hub listener after an OAuth code exchange completes.
       */
      initAuth: async () => {
        set({ isLoading: true, error: null })
        try {
          const cognitoUser = await getCurrentUser()
          const session = await fetchAuthSession()
          const payload = session.tokens?.idToken?.payload

          set({
            user: {
              userId: (payload?.['custom:user_id'] as string) ?? cognitoUser.userId,
              username: cognitoUser.username,
              email: (payload?.email as string) ?? cognitoUser.username,
              givenName: (payload?.given_name as string) ?? null,
            },
            isAuthenticated: true,
            isLoading: false,
          })
        } catch {
          // No active session — not an error, just unauthenticated
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },

      /**
       * Redirect to the Cognito Hosted UI.
       * Cognito handles sign-in, sign-up, forgot password, and MFA.
       * After auth, Cognito redirects back to VITE_COGNITO_REDIRECT_SIGN_IN.
       */
      signIn: async () => {
        await signInWithRedirect()
      },

      signOut: async () => {
        // isSigningOut blocks AuthGuard from calling signIn() while the OAuth
        // logout redirect is in flight (Amplify fires Hub 'signedOut' before
        // the browser navigates to the Cognito logout endpoint).
        set({ isLoading: true, isSigningOut: true })
        try {
          await signOut()
          set({ user: null, isAuthenticated: false, isLoading: false, isSigningOut: false })
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Sign out failed',
            isLoading: false,
            isSigningOut: false,
          })
        }
      },

      clearAuth: () => set({ user: null, isAuthenticated: false, isLoading: false }),
      setError: (message) => set({ error: message }),
      clearError: () => set({ error: null }),
    }),
    { name: 'auth-store' }
  )
)
